import React, { useEffect, useMemo, useState } from 'react';
import { UserCog, X, Plus, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

export const DeptManagersButton = ({ employees = [], onDone }) => {
  const [open, setOpen] = useState(false);
  const [depts, setDepts] = useState([]);
  const [dept, setDept] = useState('');
  const [info, setInfo] = useState(null);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && !depts.length) api.get('/api/admin/departments').then(r => setDepts((r.data || []).map(d => d.department).filter(Boolean))).catch(() => {});
  }, [open, depts.length]);

  const load = (d) => api.get('/api/dept-managers', { params: { department: d } }).then(r => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { if (dept) { setInfo(null); load(dept); } }, [dept]);

  const candidates = useMemo(() => employees
    .filter(e => e.employment_status === 'Active' && !(info?.managers || []).some(m => m.id === e.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '')), [employees, info]);

  const change = async (body, msg) => {
    setBusy(true);
    try {
      const r = await api.post('/api/dept-managers', { department: dept, ...body });
      toast({ title: msg, description: `${r.data.updated} staff updated` });
      setPick(''); load(dept); onDone?.();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1.5" data-testid="dept-managers-btn">
        <UserCog className="h-3.5 w-3.5" /> Department managers
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="dept-managers-dialog">
          <DialogHeader><DialogTitle>Department reporting managers</DialogTitle></DialogHeader>
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger data-testid="dept-managers-dept-select"><SelectValue placeholder="Choose a department" /></SelectTrigger>
            <SelectContent>{depts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          {dept && !info && <Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" />}
          {info && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">{info.staff_total} current staff in {dept}. Changes apply to all of them.</p>
              <div className="space-y-1.5" data-testid="dept-managers-list">
                {info.managers.length === 0 && <p className="text-sm text-gray-500">No managers assigned yet.</p>}
                {info.managers.map(m => (
                  <div key={m.id} className="flex items-center justify-between rounded-md border px-3 py-2" data-testid={`dept-manager-row-${m.employee_code}`}>
                    <div><p className="text-sm font-medium">{m.name}</p>
                      <p className="text-[11px] text-gray-500">{m.employee_code} · manages {m.staff_count} of {info.staff_total}</p></div>
                    <Button size="sm" variant="ghost" disabled={busy} className="text-rose-600"
                      onClick={() => change({ remove: [m.id] }, `${m.name} removed`)} data-testid={`dept-manager-remove-${m.employee_code}`}>
                      <X className="h-4 w-4 mr-1" /> Remove
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Select value={pick} onValueChange={setPick}>
                  <SelectTrigger className="flex-1" data-testid="dept-manager-add-select"><SelectValue placeholder="Add a manager…" /></SelectTrigger>
                  <SelectContent>{candidates.map(e => <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>)}</SelectContent>
                </Select>
                <Button disabled={!pick || busy} onClick={() => change({ add: [pick] }, 'Manager added')} data-testid="dept-manager-add-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add to all
                </Button>
              </div>
              {info.no_manager.length > 0 && (
                <p className="text-[11px] text-amber-700 bg-amber-50 rounded p-2" data-testid="dept-no-manager">
                  No manager: {info.no_manager.map(s => s.name).join(', ')}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
